import { useMemo, useState } from 'react';
import { AsyncBoundary, Button, Card, Chip, Drawer, useToast } from '@kollez/design-system';
import { formatIndianDate } from '@kollez/shared/format';
import { useNexieDrafts, useDiscardDraft, useSendDraft, type DraftItem } from '@/lib/queries';

/**
 * NexieDrafts (F-078) — the review inbox for every Nexie-authored draft.
 *
 * Nexie NEVER sends on its own. This page is where a human — the principal reviewing a weekly
 * brief, a TPO reviewing a reminder — approves what Nexie wrote before any notification fans out.
 * Kollez rule: drafts-never-send preserved end-to-end. The "Send" button calls /messages/send.
 */

type Status = 'draft' | 'sent' | 'discarded';

interface WeeklyBriefPayload {
  narrative: string;
  bullets: string[];
  suggestedNextSteps?: string[];
  stats?: {
    drivesRun: number;
    applications: number;
    offersReleased: number;
    offersAccepted: number;
    medianCtcInr: number;
  };
}

function isWeeklyBrief(p: unknown): p is WeeklyBriefPayload {
  return !!p && typeof p === 'object' && 'narrative' in (p as Record<string, unknown>);
}

function summarize(d: DraftItem): string {
  if (isWeeklyBrief(d.payload)) {
    const s = d.payload.stats;
    if (!s) return d.payload.narrative.slice(0, 140);
    return `${s.applications} apps · ${s.offersAccepted} accepted · ₹${(s.medianCtcInr / 100_000).toFixed(1)} LPA median`;
  }
  return '(payload preview unavailable)';
}

function kindLabel(kind: string): string {
  return kind.replace(/_/g, ' ');
}

export default function NexieDrafts() {
  const [status, setStatus] = useState<Status>('draft');
  const [selected, setSelected] = useState<DraftItem | null>(null);
  const q = useNexieDrafts(status);
  const discard = useDiscardDraft();
  const send = useSendDraft();
  const toast = useToast();

  const rows = q.data?.drafts ?? [];
  const counts = useMemo(() => ({ total: rows.length }), [rows]);

  async function handleSend(draft: DraftItem) {
    try {
      const r = await send.mutateAsync({ draftId: draft._id });
      toast.show({
        title: 'Sent',
        message: `Fanned out to ${r.recipientCount} recipient(s), ${r.notificationCount} notifications.`,
      });
      setSelected(null);
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message
        ?? 'Send failed';
      toast.show({ title: 'Send failed', message: msg });
    }
  }

  async function handleDiscard(draft: DraftItem) {
    if (!confirm('Discard this draft? Nexie will not send it.')) return;
    await discard.mutateAsync({ id: draft._id });
    toast.show({ title: 'Discarded', message: 'Draft removed from your review queue.' });
    setSelected(null);
  }

  return (
    <>
      <header className="page-header" style={{ marginBottom: 20 }}>
        <div>
          <div className="eyebrow">Nexie · Review inbox</div>
          <h1>Drafts</h1>
          <p className="sub">
            Nexie drafts — nothing goes out until you approve. Review the content, edit or discard,
            or send to the intended audience.
          </p>
        </div>
      </header>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {(['draft', 'sent', 'discarded'] as Status[]).map((s) => (
          <button
            key={s}
            type="button"
            className={`seg ${status === s ? 'active' : ''}`}
            onClick={() => setStatus(s)}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
        <span style={{ marginLeft: 'auto', alignSelf: 'center', color: 'rgb(var(--ink-tertiary))', fontSize: 13 }}>
          {counts.total} {counts.total === 1 ? 'draft' : 'drafts'}
        </span>
      </div>

      <AsyncBoundary
        kind="table"
        loading={q.isLoading}
        error={q.error}
        empty={!q.isLoading && rows.length === 0}
        emptyTitle={status === 'draft' ? 'No pending drafts' : `No ${status} drafts`}
        emptyHint={status === 'draft' ? 'Nexie creates drafts when you generate a weekly brief, a reminder, or an outreach note.' : undefined}
      >
        <div style={{ display: 'grid', gap: 12 }}>
          {rows.map((d) => (
            <Card key={d._id} style={{ padding: 16 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                    <Chip>{kindLabel(d.kind)}</Chip>
                    <Chip tone={d.status === 'draft' ? 'info' : d.status === 'sent' ? 'success' : 'default'}>
                      {d.status}
                    </Chip>
                    <span style={{ marginLeft: 'auto', color: 'rgb(var(--ink-tertiary))', fontSize: 12 }}>
                      {formatIndianDate(new Date(d.createdAt))}
                    </span>
                  </div>
                  <div style={{ fontWeight: 500, marginBottom: 4 }}>{summarize(d)}</div>
                  <div style={{ fontSize: 12, color: 'rgb(var(--ink-tertiary))' }}>
                    {d.citations?.length ?? 0} citation(s) · Nexie draft
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="ghost" onClick={() => setSelected(d)}>Review</Button>
                  {d.status === 'draft' && (
                    <>
                      <Button
                        onClick={() => handleSend(d)}
                        disabled={send.isPending}
                      >
                        {send.isPending ? 'Sending…' : 'Send'}
                      </Button>
                      <Button variant="ghost" onClick={() => handleDiscard(d)}>Discard</Button>
                    </>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </AsyncBoundary>

      <Drawer open={!!selected} onOpenChange={(v) => !v && setSelected(null)} size="lg">
        {selected && (
          <div style={{ display: 'grid', gap: 12 }}>
            <div className="eyebrow">Nexie draft · {kindLabel(selected.kind)}</div>
            <h2 style={{ margin: 0 }}>{formatIndianDate(new Date(selected.createdAt))}</h2>
            {isWeeklyBrief(selected.payload) ? (
              <>
                <p style={{ lineHeight: 1.6 }}>{selected.payload.narrative}</p>
                {selected.payload.bullets?.length > 0 && (
                  <div>
                    <h3 style={{ fontSize: 14, marginBottom: 6 }}>Key points</h3>
                    <ul style={{ paddingLeft: 18, margin: 0 }}>
                      {selected.payload.bullets.map((b, i) => (
                        <li key={i} style={{ marginBottom: 4 }}>{b}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {selected.payload.suggestedNextSteps && selected.payload.suggestedNextSteps.length > 0 && (
                  <div>
                    <h3 style={{ fontSize: 14, marginBottom: 6 }}>Suggested next steps</h3>
                    <ul style={{ paddingLeft: 18, margin: 0 }}>
                      {selected.payload.suggestedNextSteps.map((s, i) => (
                        <li key={i} style={{ marginBottom: 4 }}>{s}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <pre style={{
                background: 'rgb(var(--bg-secondary))',
                padding: 12,
                borderRadius: 6,
                overflow: 'auto',
                fontSize: 12,
                maxHeight: 360,
              }}>
                {JSON.stringify(selected.payload, null, 2)}
              </pre>
            )}
            {selected.status === 'draft' && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8, borderTop: '1px solid rgb(var(--line-secondary))', paddingTop: 12 }}>
                <Button onClick={() => handleSend(selected)} disabled={send.isPending}>
                  {send.isPending ? 'Sending…' : 'Approve & send'}
                </Button>
                <Button variant="ghost" onClick={() => handleDiscard(selected)}>Discard</Button>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </>
  );
}
